import { ApiPermission } from "@bvhi/backend-api";
import { Box } from "@bvhi/ui-base";
import { LinkProps } from "@tanstack/react-router";
import { useMemo } from "react";
import { useAuthContext } from "../../../context/use-auth-context";
import { usePermissions } from "../../../hooks/use-permissions";
import { DeleteCell } from "./delete-cell";
import { DetailCell } from "./detail-cell";
import { DownloadCell } from "./download-cell";

interface Props {
  deleteActionDisabled?: boolean;
  detailActionDisabled?: boolean;
  downloadActionDisabled?: boolean;
  id?: string;
  showDeleteAction?: boolean;
  showDetailAction?: boolean;
  showDownloadAction?: boolean;
  deletePermission?: ApiPermission;
  detailPermission?: ApiPermission;
  downloadPermission?: ApiPermission;
  detailRoute?: LinkProps["to"];
  detailParams?: LinkProps["params"];
  handleDelete?: (id: string) => void;
  handleDownload?: (id: string) => void;
}

export const ActionCells = ({
  id,
  deleteActionDisabled = false,
  detailActionDisabled = false,
  downloadActionDisabled = false,
  showDeleteAction = false,
  showDetailAction = false,
  showDownloadAction = false,
  deletePermission,
  detailPermission,
  detailParams,
  downloadPermission,
  detailRoute,
  handleDelete,
  handleDownload,
}: Props) => {
  const { currentUser } = useAuthContext();
  const { hasPermissionTo } = usePermissions();

  const canDelete = useMemo(() => {
    if (!deletePermission) return false;
    return hasPermissionTo([deletePermission]);
  }, [deletePermission, hasPermissionTo]);

  const canOpenDetailView = useMemo(() => {
    if (!detailPermission) return false;
    return hasPermissionTo([detailPermission]);
  }, [detailPermission, hasPermissionTo]);

  const canDownload = useMemo(() => {
    if (!downloadPermission) return false;
    return hasPermissionTo([downloadPermission]);
  }, [downloadPermission, hasPermissionTo]);

  const isOwnCell = useMemo(() => {
    return currentUser?.id === id;
  }, [currentUser, id]);

  return (
    <Box>
      {showDetailAction && detailRoute && detailParams && canOpenDetailView && id && (
        <DetailCell disabled={!canOpenDetailView || detailActionDisabled} params={detailParams} route={detailRoute} />
      )}
      {showDeleteAction && handleDelete && canDelete && id && (
        <DeleteCell disabled={isOwnCell || deleteActionDisabled} handleDelete={() => handleDelete(id)} />
      )}
      {showDownloadAction && canDownload && id && handleDownload && (
        <DownloadCell disabled={downloadActionDisabled} handleDownload={() => handleDownload(id)} />
      )}
    </Box>
  );
};
